import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse } from './baseApi';

/** Prefer backend message from BaseApiService errors (`HTTP error! status: N - <message>`). */
function getJobWorkErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error) || !error.message?.trim()) return fallback;
  const msg = error.message.trim();
  const sep = ' - ';
  const idx = msg.indexOf(sep);
  if (idx >= 0) {
    const backend = msg.slice(idx + sep.length).trim();
    if (backend) return backend;
  }
  return msg;
}

function toastJobWorkError(error: unknown, fallback: string): string {
  const message = getJobWorkErrorMessage(error, fallback);
  toast.error(message);
  return message;
}

export type JobWorkPartyType = 'VENDOR' | 'BRANCH';
/** DRAFT → APPROVED → PARTIAL → COMPLETED (RELEASED/CLOSED = legacy aliases) */
export type JobWorkOrderStatus =
  | 'DRAFT'
  | 'APPROVED'
  | 'RELEASED'
  | 'PARTIAL'
  | 'COMPLETED'
  | 'CLOSED'
  | 'CANCELLED';
export type JobWorkJobType = 'RAW_MATERIAL' | 'BOP' | 'WIP' | 'FINISHED_GOOD' | 'CAPITAL_GOOD';
export type JobWorkChallanDirection = 'SEND' | 'RECEIVE';
export type JobWorkChallanStatus = 'DRAFT' | 'DISPATCHED' | 'RECEIVED' | 'CANCELLED';

export const JOB_WORK_PARTY_TYPES: JobWorkPartyType[] = ['VENDOR', 'BRANCH'];
export const JOB_WORK_ORDER_STATUSES: JobWorkOrderStatus[] = [
  'DRAFT',
  'APPROVED',
  'PARTIAL',
  'COMPLETED',
  'CANCELLED',
];

export function isJobWorkOpenForReceive(status?: string | null): boolean {
  const s = String(status ?? '').toUpperCase();
  return s === 'APPROVED' || s === 'RELEASED' || s === 'PARTIAL';
}

export function isJobWorkEditable(status?: string | null): boolean {
  return String(status ?? '').toUpperCase() === 'DRAFT';
}
export const JOB_WORK_JOB_TYPES: JobWorkJobType[] = [
  'RAW_MATERIAL',
  'BOP',
  'WIP',
  'FINISHED_GOOD',
  'CAPITAL_GOOD',
];

export function jobWorkUsesRawItem(jobType: JobWorkJobType | string): boolean {
  return jobType === 'RAW_MATERIAL' || jobType === 'BOP' || jobType === 'CAPITAL_GOOD';
}

/** Maps to `job_work_order_issue_line` */
export interface JobWorkOrderIssueLineDto {
  id?: number;
  jobType: JobWorkJobType;
  rawItemId?: number | null;
  /** Response-only */
  rawItemName?: string | null;
  endgoodId?: number | null;
  /** Response-only */
  endgoodName?: string | null;
  plannedQty: number;
  issuedQty?: number;
  receivedBackQty?: number;
  scrapQty?: number;
  openAtJwQty?: number;
  openToIssueQty?: number;
  uom?: string | null;
  heatNo?: string | null;
  notes?: string | null;
}

/** Maps to `job_work_order_output_line` */
export interface JobWorkOrderOutputLineDto {
  id?: number;
  jobType?: JobWorkJobType;
  rawItemId?: number | null;
  /** Response-only */
  rawItemName?: string | null;
  endgoodId?: number | null;
  /** Response-only */
  endgoodName?: string | null;
  /** End process — till which process the job work was performed. */
  processId?: number | null;
  /** Response-only */
  processName?: string | null;
  plannedQty: number;
  receivedQty?: number;
  uom?: string | null;
  conversionRate?: number | null;
  notes?: string | null;
}

/**
 * Maps to `job_work_order_work_order` — one production WO linked to a JW with qty.
 */
export interface JobWorkOrderWorkOrderDto {
  id?: number;
  workOrderId: number;
  /** Qty of this WO included in this JW (may be less than full WIP qty). */
  qty: number;
  /** Response-only */
  workOrderNumber?: string | null;
  workOrderQty?: number | null;
}

/**
 * Maps to `job_work_order` (+ linked rows in `job_work_order_work_order`).
 * Header holds endgood + start process; production WOs are many-to-many via workOrders.
 */
export interface JobWorkOrderDto {
  id?: number;
  jwOrderNo?: string;
  branchId: number;
  /** Response-only */
  branchName?: string | null;
  partyType: JobWorkPartyType;
  supplierId?: number | null;
  /** Response-only */
  supplierName?: string | null;
  toBranchId?: number | null;
  /** Response-only */
  toBranchName?: string | null;
  status?: JobWorkOrderStatus;
  /** Start process for this JW (required). */
  processId: number;
  /** Response-only */
  processName?: string | null;
  /** Endgood this JW covers (required; one per order). */
  endgoodId: number;
  /** Response-only */
  endgoodName?: string | null;
  /** Linked production WOs with qty (same endgood). */
  workOrders?: JobWorkOrderWorkOrderDto[] | null;
  orderDate?: string;
  expectedReturnDate?: string | null;
  notes?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
  issueLines: JobWorkOrderIssueLineDto[];
  outputLines?: JobWorkOrderOutputLineDto[];
}

/** Maps to `job_work_challan_line` */
export interface JobWorkChallanLineDto {
  id?: number;
  orderIssueLineId?: number | null;
  orderOutputLineId?: number | null;
  linkedSendLineId?: number | null;
  jobType?: JobWorkJobType | null;
  rawItemId?: number | null;
  rawItemName?: string | null;
  endgoodId?: number | null;
  endgoodName?: string | null;
  qty: number;
  scrapQty?: number;
  rejectQty?: number;
  uom?: string | null;
  rate?: number | null;
  amount?: number | null;
  heatNo?: string | null;
  storeId?: number | null;
  storeName?: string | null;
  stockPosted?: boolean;
  notes?: string | null;
}

/** Maps to `job_work_challan` */
export interface JobWorkChallanDto {
  id?: number;
  challanNo?: string;
  direction?: JobWorkChallanDirection;
  jobWorkOrderId: number;
  jwOrderNo?: string | null;
  linkedSendChallanId?: number | null;
  linkedSendChallanNo?: string | null;
  branchId?: number;
  branchName?: string | null;
  partyType?: JobWorkPartyType;
  supplierId?: number | null;
  supplierName?: string | null;
  toBranchId?: number | null;
  toBranchName?: string | null;
  status?: JobWorkChallanStatus;
  challanDate?: string;
  datetimeOfSupply?: string | null;
  returnDueDate?: string | null;
  vehNo?: string | null;
  transporter?: string | null;
  placeOfSupply?: string | null;
  modeOfDispatch?: string | null;
  documentNo?: string | null;
  ewayBillNo?: string | null;
  totalAmount?: number;
  grandTotalTax?: number;
  grandTotal?: number;
  notes?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
  createdAt?: string;
  updatedAt?: string;
  lines: JobWorkChallanLineDto[];
}

/** GET /jobwork/lookup/processes */
export interface JobWorkProcessLookupDto {
  processId: number;
  processName: string;
  sequenceOrder?: number;
  processStepId?: number;
  processFlowId?: number;
}

/** GET /jobwork/lookup/wip-work-orders */
export interface JobWorkWipWorkOrderDto {
  workOrderId: number;
  workOrderNumber: string;
  endgoodId: number;
  endgoodName?: string;
  branchId?: number;
  startProcessId?: number;
  startProcessName?: string;
  fromProcessName?: string;
  availableWipQty?: number;
  soNumber?: string;
  priority?: string;
}

/** GET /jobwork/pending — open qty still at job worker */
export interface JobWorkPendingLineDto {
  sendLineId: number;
  orderIssueLineId?: number | null;
  rawItemId?: number | null;
  rawItemName?: string | null;
  endgoodId?: number | null;
  endgoodName?: string | null;
  jobType?: string | null;
  sentQty?: number;
  receivedQty?: number;
  scrapQty?: number;
  openQty?: number;
  heatNo?: string | null;
}

export interface JobWorkPendingDto {
  sendChallanId: number;
  sendChallanNo?: string | null;
  jobWorkOrderId: number;
  jwOrderNo?: string | null;
  branchId?: number;
  branchName?: string | null;
  partyType?: string | null;
  supplierId?: number | null;
  supplierName?: string | null;
  toBranchId?: number | null;
  toBranchName?: string | null;
  challanDate?: string | null;
  returnDueDate?: string | null;
  overdue?: boolean;
  overdueDays?: number;
  totalSentQty?: number;
  totalOpenQty?: number;
  lines: JobWorkPendingLineDto[];
}

class JobWorkApiService extends BaseApiService {
  private unwrapList<T>(response: ApiResponse<T[]> | T[]): T[] {
    if (Array.isArray(response)) return response;
    return Array.isArray(response?.data) ? response.data : [];
  }

  private unwrapOne<T>(response: ApiResponse<T> | T): T | null {
    if (response && typeof response === 'object' && 'success' in response) {
      return response.success && response.data ? response.data : null;
    }
    return (response as T) ?? null;
  }

  private responseMessage(response: unknown, fallback: string): string {
    if (response && typeof response === 'object' && 'message' in response) {
      const msg = String((response as { message?: string }).message ?? '').trim();
      if (msg) return msg;
    }
    return fallback;
  }

  async listOrders(branchId?: number, status?: string): Promise<ApiResponse<JobWorkOrderDto[]>> {
    try {
      const params = new URLSearchParams();
      if (branchId != null) params.append('branchId', String(branchId));
      if (status) params.append('status', status);
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto[]> | JobWorkOrderDto[]>(
        `${API_ENDPOINTS.JOBWORK.ORDERS}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.GET }
      );
      const data = this.unwrapList(response);
      return {
        success: true,
        message: 'Job work orders fetched',
        data,
      };
    } catch (error) {
      toastJobWorkError(error, 'Failed to fetch job work orders');
      return { success: false, message: getJobWorkErrorMessage(error, 'Failed to fetch'), data: [] };
    }
  }

  async getOrderById(id: number | string): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        API_ENDPOINTS.JOBWORK.ORDER_BY_ID(id),
        { method: HTTP_METHODS.GET }
      );
      const data = this.unwrapOne(response);
      return {
        success: Boolean(data),
        message: data ? 'Job work order fetched' : 'Job work order not found',
        data: data as JobWorkOrderDto,
      };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to fetch job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  async createOrder(payload: JobWorkOrderDto): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        API_ENDPOINTS.JOBWORK.ORDERS,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(payload),
        }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to create job work order');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkOrderDto };
      }
      toast.success('Job work order created');
      return { success: true, message: 'Created', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to create job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  async updateOrder(id: number | string, payload: JobWorkOrderDto): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        API_ENDPOINTS.JOBWORK.ORDER_BY_ID(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(payload),
        }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to update job work order');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkOrderDto };
      }
      toast.success('Job work order updated');
      return { success: true, message: 'Updated', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to update job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  async approveOrder(id: number | string, updatedBy?: number): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const params = new URLSearchParams();
      if (updatedBy != null) params.append('updatedBy', String(updatedBy));
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        `${API_ENDPOINTS.JOBWORK.ORDER_APPROVE(id)}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.POST }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to approve job work order');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkOrderDto };
      }
      toast.success('Job work order approved (outward challan created)');
      return { success: true, message: 'Approved', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to approve job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  /** @deprecated Prefer approveOrder — backend aliases release → approve */
  async releaseOrder(id: number | string, updatedBy?: number): Promise<ApiResponse<JobWorkOrderDto>> {
    return this.approveOrder(id, updatedBy);
  }

  async closeOrder(id: number | string, updatedBy?: number): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const params = new URLSearchParams();
      if (updatedBy != null) params.append('updatedBy', String(updatedBy));
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        `${API_ENDPOINTS.JOBWORK.ORDER_CLOSE(id)}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.POST }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to complete job work order');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkOrderDto };
      }
      toast.success('Job work order completed');
      return { success: true, message: 'Completed', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to complete job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  async cancelOrder(
    id: number | string,
    reason?: string,
    updatedBy?: number
  ): Promise<ApiResponse<JobWorkOrderDto>> {
    try {
      const params = new URLSearchParams();
      if (reason) params.append('reason', reason);
      if (updatedBy != null) params.append('updatedBy', String(updatedBy));
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkOrderDto> | JobWorkOrderDto>(
        `${API_ENDPOINTS.JOBWORK.ORDER_CANCEL(id)}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.POST }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to cancel job work order');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkOrderDto };
      }
      toast.success('Job work order cancelled');
      return { success: true, message: 'Cancelled', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to cancel job work order');
      return { success: false, message, data: null as unknown as JobWorkOrderDto };
    }
  }

  /** Processes for an endgood from its active BOM process flow. */
  async lookupProcessesForEndgood(endgoodId: number): Promise<ApiResponse<JobWorkProcessLookupDto[]>> {
    try {
      const params = new URLSearchParams({ endgoodId: String(endgoodId) });
      const response = await this.makeRequest<
        ApiResponse<JobWorkProcessLookupDto[]> | JobWorkProcessLookupDto[]
      >(`${API_ENDPOINTS.JOBWORK.LOOKUP_PROCESSES}?${params}`, { method: HTTP_METHODS.GET });
      return {
        success: true,
        message: 'Processes retrieved',
        data: this.unwrapList(response),
      };
    } catch (error) {
      toastJobWorkError(error, 'Failed to load processes for endgood');
      return { success: false, message: getJobWorkErrorMessage(error, 'Failed to load'), data: [] };
    }
  }

  /** WIP work orders for endgood ready for the selected start process. */
  async lookupWipWorkOrders(
    endgoodId: number,
    processId: number,
    branchId?: number | null
  ): Promise<ApiResponse<JobWorkWipWorkOrderDto[]>> {
    try {
      const params = new URLSearchParams({
        endgoodId: String(endgoodId),
        processId: String(processId),
      });
      if (branchId != null) params.append('branchId', String(branchId));
      const response = await this.makeRequest<
        ApiResponse<JobWorkWipWorkOrderDto[]> | JobWorkWipWorkOrderDto[]
      >(`${API_ENDPOINTS.JOBWORK.LOOKUP_WIP_WORK_ORDERS}?${params}`, { method: HTTP_METHODS.GET });
      return {
        success: true,
        message: 'WIP work orders retrieved',
        data: this.unwrapList(response),
      };
    } catch (error) {
      toastJobWorkError(error, 'Failed to load WIP work orders');
      return { success: false, message: getJobWorkErrorMessage(error, 'Failed to load'), data: [] };
    }
  }

  async getPending(
    branchId?: number | null,
    overdueOnly = false
  ): Promise<ApiResponse<JobWorkPendingDto[]>> {
    try {
      const params = new URLSearchParams();
      if (branchId != null) params.append('branchId', String(branchId));
      if (overdueOnly) params.append('overdueOnly', 'true');
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkPendingDto[]> | JobWorkPendingDto[]>(
        `${API_ENDPOINTS.JOBWORK.PENDING}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.GET }
      );
      return {
        success: true,
        message: 'Pending JW retrieved',
        data: this.unwrapList(response),
      };
    } catch (error) {
      toastJobWorkError(error, 'Failed to load pending job work');
      return { success: false, message: getJobWorkErrorMessage(error, 'Failed to load'), data: [] };
    }
  }

  async getChallansByOrder(orderId: number | string): Promise<ApiResponse<JobWorkChallanDto[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<JobWorkChallanDto[]> | JobWorkChallanDto[]>(
        API_ENDPOINTS.JOBWORK.CHALLANS_BY_ORDER(orderId),
        { method: HTTP_METHODS.GET }
      );
      return {
        success: true,
        message: 'JW challans retrieved',
        data: this.unwrapList(response),
      };
    } catch (error) {
      toastJobWorkError(error, 'Failed to load JW challans');
      return { success: false, message: getJobWorkErrorMessage(error, 'Failed to load'), data: [] };
    }
  }

  async createReceive(payload: JobWorkChallanDto): Promise<ApiResponse<JobWorkChallanDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<JobWorkChallanDto> | JobWorkChallanDto>(
        API_ENDPOINTS.JOBWORK.CHALLANS_RECEIVE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(payload),
        }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to create receive challan');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkChallanDto };
      }
      return { success: true, message: 'Receive challan created', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to create receive challan');
      return { success: false, message, data: null as unknown as JobWorkChallanDto };
    }
  }

  async confirmReceive(
    id: number | string,
    updatedBy?: number
  ): Promise<ApiResponse<JobWorkChallanDto>> {
    try {
      const params = new URLSearchParams();
      if (updatedBy != null) params.append('updatedBy', String(updatedBy));
      const qs = params.toString();
      const response = await this.makeRequest<ApiResponse<JobWorkChallanDto> | JobWorkChallanDto>(
        `${API_ENDPOINTS.JOBWORK.CHALLAN_RECEIVE_CONFIRM(id)}${qs ? `?${qs}` : ''}`,
        { method: HTTP_METHODS.POST }
      );
      const data = this.unwrapOne(response);
      if (!data) {
        const message = this.responseMessage(response, 'Failed to confirm receive challan');
        toast.error(message);
        return { success: false, message, data: null as unknown as JobWorkChallanDto };
      }
      toast.success('Material received');
      return { success: true, message: 'Received', data };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to confirm receive challan');
      return { success: false, message, data: null as unknown as JobWorkChallanDto };
    }
  }

  /**
   * Create + confirm inward receive challan(s) for open send lines.
   * Groups lines by send challan; qty may be partial.
   */
  async receiveAgainstPending(
    jobWorkOrderId: number,
    lines: Array<{
      sendChallanId: number;
      sendLineId: number;
      qty: number;
      scrapQty?: number;
      orderOutputLineId?: number | null;
    }>,
    updatedBy?: number
  ): Promise<ApiResponse<JobWorkChallanDto[]>> {
    type ReceiveLineInput = {
      sendChallanId: number;
      sendLineId: number;
      qty: number;
      scrapQty?: number;
      orderOutputLineId?: number | null;
    };

    const bySend: Record<string, ReceiveLineInput[]> = {};
    for (const line of lines) {
      if (!(Number(line.qty) > 0)) continue;
      const key = String(line.sendChallanId);
      if (!bySend[key]) bySend[key] = [];
      bySend[key].push(line);
    }

    const sendChallanIds = Object.keys(bySend);
    if (sendChallanIds.length === 0) {
      toast.error('Enter receive qty for at least one line');
      return { success: false, message: 'No qty', data: [] };
    }

    const confirmed: JobWorkChallanDto[] = [];
    try {
      for (const sendChallanIdStr of sendChallanIds) {
        const sendChallanId = Number(sendChallanIdStr);
        const sendLines = bySend[sendChallanIdStr] ?? [];
        const createRes = await this.createReceive({
          jobWorkOrderId,
          linkedSendChallanId: sendChallanId,
          createdBy: updatedBy ?? null,
          lines: sendLines.map((l: ReceiveLineInput) => ({
            linkedSendLineId: l.sendLineId,
            qty: Number(l.qty),
            scrapQty: Number(l.scrapQty) > 0 ? Number(l.scrapQty) : 0,
            orderOutputLineId: l.orderOutputLineId ?? null,
          })),
        });
        if (!createRes.success || !createRes.data?.id) {
          return { success: false, message: 'Failed to create receive', data: confirmed };
        }
        const confirmRes = await this.confirmReceive(createRes.data.id, updatedBy);
        if (!confirmRes.success || !confirmRes.data) {
          return { success: false, message: 'Failed to confirm receive', data: confirmed };
        }
        confirmed.push(confirmRes.data);
      }
      return { success: true, message: 'Received', data: confirmed };
    } catch (error) {
      const message = toastJobWorkError(error, 'Failed to receive material');
      return { success: false, message, data: confirmed };
    }
  }
}

export const jobWorkApiService = new JobWorkApiService();
